import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Drawer, Grid, Stack, Typography } from "@mui/material"
import { ExpandMoreIcon } from "components/Icons"
import PreviewCard from "components/PreviewCard"
import { capitalize } from "lodash"
import FeildSkeleton from "pages/Provider/Employees/Person/FieldSkeleton"
import { getStatusColor } from "utils"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import AddSchoolForm from "./Form/AddSchoolForm"
import UpdateSchoolApplication from "./Form/UpdateSchoolApplication"
import { useSession } from "components/Providers"
import { useDispatch, useSelector } from "react-redux"
import { getTypes } from "store/lookup"
import { startEnrollmentProcess } from "store/application/student-enrollment"
import { save as sendNotification } from "store/notification/notification"
import EnrollmentProgress from "./EnrollmentProgress/index"
import { hasFullPermission } from "store"
import * as snackbar from "utils/snackbar"

// notification_template.code — the row there declares what the payload below must carry
const ENROLLMENT_STARTED_TEMPLATE = 'enrollment-notification-start'

export default function StudentSchools({ applicationRecord, setApplicationRecord, clientSchools }) {
  const { district, campus } = useSession()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [openSchoolDrawer, setOpenSchoolDrawer] = useState(false)
  const [openUpdateSchoolDrawer, setOpenUpdateSchoolDrawer] = useState(false)
  const applicationStatusTypes = useSelector(state => state.lookup.types)?.filter(item => item.category === 'applicationStatus') || []
  const [openAuditDrawer, setOpenAuditDrawer] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null)

  useEffect(() => {
    if (!applicationStatusTypes) {
      dispatch(getTypes());
    }
  }, [applicationStatusTypes, dispatch]);

  const getApplicationDetails = (item) => [
    { key: "Application ID", value: item?.applicationId ? item.applicationId : '' },
    { key: "Lottery Status", value: item?.lotteryStatus ? item.lotteryStatus : '' },
    { key: "Lottery Rank", value: item?.lotteryRank ? item.lotteryRank : '' },
    { key: "Notes", value: item?.notes ? item.notes : '' },

  ]

  const notifyEnrollmentStarted = async (applicationSchoolId) => {
    const result = await dispatch(sendNotification({
      templateCode: ENROLLMENT_STARTED_TEMPLATE,
      payload: [{ _id: applicationSchoolId }],
    }));

    if (result?.error) {
      snackbar.error(result.error?.data?.message || t('Enrollment started, but the family could not be notified.'));
      return;
    }

    snackbar.success(t('Enrollment started and the family has been notified.'));
  };

  const handleOpenEnrollment = (item) => {
    if (!item?.enrollmentPackage) {
      dispatch(startEnrollmentProcess({ id: item.id })).then((response) => {
        if (response?.error) {
          snackbar.error(response.error?.data?.message || t('Could not start the enrollment.'));
          return;
        }

        const finalized = response?.payload?.finalized;
        setApplicationRecord(prev => ({
          ...prev,
          applicationSchools: prev.applicationSchools.map(school =>
            school.id === item.id
              ? { ...school, enrollmentPackage: { ...(school.enrollmentPackage || {}), finalized } }
              : school
          )
        }));
        notifyEnrollmentStarted(item.id);
      });
    } else {
      setSelectedSchool(item);
      setOpenAuditDrawer(true);
    }
  };

  const handleOpenUpdate = (item) => {
    setSelectedSchool(item);
    setOpenUpdateSchoolDrawer(true);
  };

  return (
    <Box p={2}>
      <Stack spacing={2} mb={2} direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight='600'>
          {t("Schools Applied")}
        </Typography>
        {district?.isServiceProvider && hasFullPermission('application.application') && (
          <Button variant="contained" size="small" onClick={() => { setOpenSchoolDrawer(true) }}>
            {t("Add School")}
          </Button>
        )}
      </Stack>
      <Stack spacing={2}>
        {[...(applicationRecord.applicationSchools || [])]
          .sort((a, b) =>
            a.school?.id === campus?.schoolId ? -1 : b.school?.id === campus?.schoolId ? 1 : 0
          )
          .map((item, idx) => {
            const isCampus = item.school?.id === campus?.schoolId;
            const statusColor = getStatusColor(item.status);
            const isAccepted = ['accepted', 'enrolled', 'approved', 'inprogress'].includes((item.status || "").toLowerCase());

            return (
              <Accordion
                key={item.id || idx}
                defaultExpanded={isCampus}
                disableGutters
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  mb: 2,
                  boxShadow: 0
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{item?.school?.name}</Typography>
                  <Chip label={capitalize(item.status)} size="small" sx={{ ml: 2 }} color={statusColor} />
                </AccordionSummary>
                <AccordionDetails>
                  <PreviewCard
                    title="Application Details"
                    size="large"
                    {...(campus?.schoolId === item?.school?.id && {
                      buttonLabel: t('More'),
                      buttonClick: () => {
                        handleOpenUpdate(item);
                      },
                      buttonNavigate: true,
                    })}
                    buttonPermission={'application.application'}
                  >
                    <Grid container spacing={2}>
                      {getApplicationDetails(item).map((detail, index) => (
                        <Grid
                          key={index}
                          item
                          sm={detail.key === "Notes" ? 12 : 4}
                          xs={detail.key === "Notes" ? 12 : 4}
                          md={detail.key === "Notes" ? 12 : 4}
                        >
                          <Typography className={`grid-table-title`} variant="body2" sx={{ fontSize: '12px' }}>
                            {t(detail.key)}
                          </Typography>
                          <Typography variant="body1" className={`grid-table-title-value`}>
                            {detail.value ? detail.value : <FeildSkeleton />} &nbsp;
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </PreviewCard>

                  {(hasFullPermission('application.application') || hasFullPermission('application.nurse') || hasFullPermission('application.special')) && isAccepted && isCampus && (
                    <Box mt={3}>
                      <Alert
                        severity="success"
                        variant="outlined"
                        sx={{
                          bgcolor: "white",
                          border: "1px dashed",
                          borderColor: 'success.main',
                          alignItems: "center"
                        }}
                        action={
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleOpenEnrollment(item)}
                          >
                            {item?.enrollmentPackage ? t("Manage Enrollment") : t("Start Enrollment")}
                          </Button>
                        }
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {t("Student Accepted")}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {t("Review submitted documents and finalize enrollment status.")}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Stack>

      <Drawer
        anchor="right"
        open={openSchoolDrawer}
        onClose={() => setOpenSchoolDrawer(false)}
        PaperProps={{ sx: { width: 800 } }}
      >
        <AddSchoolForm applicationRecord={applicationRecord} setOpenDrawer={setOpenSchoolDrawer} setApplicationRecord={setApplicationRecord} clientSchools={clientSchools} />
      </Drawer>
      <Drawer
        anchor="right"
        open={openUpdateSchoolDrawer}
        onClose={() => setOpenUpdateSchoolDrawer(false)}
        PaperProps={{ sx: { width: 800 } }}
      >
        <UpdateSchoolApplication applicationRecord={applicationRecord} setOpenDrawer={setOpenUpdateSchoolDrawer} setApplicationRecord={setApplicationRecord} selectedSchool={selectedSchool} />
      </Drawer>

      <Drawer
        anchor="right"
        open={openAuditDrawer}
        onClose={() => setOpenAuditDrawer(false)}
        PaperProps={{ sx: { width: { xs: "100%", md: 800 } } }}
      >
        <EnrollmentProgress school={selectedSchool} student={applicationRecord} onClose={() => setOpenAuditDrawer(false)} />
      </Drawer>
    </Box>
  )
}
